import { AutocompleteField } from "@/features/FilterTable/AutocompleteField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { getAuthorized, postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";

const LoadTemplatesSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
});

export type LoadTemplatesSchemaType = z.infer<typeof LoadTemplatesSchema>;

const A7FIALoadTemplates = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    reset,
  } = useForm<LoadTemplatesSchemaType>({
    mode: "onChange",
    defaultValues: {
      reportingDate: undefined,
    },
    resolver: zodResolver(
      LoadTemplatesSchema,
    ) as Resolver<LoadTemplatesSchemaType>,
  });

  const table_name = "a7_fia_compositions";

  const { data: dateOptions } = useQuery({
    queryKey: ["a7", table_name, "dates"],
    queryFn: async () => {
      return getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/a7/${table_name}/dates`,
      )
        .then((response) => response.json())
        .then((res) => {
          return res.map((date: string) => ({
            label: date,
            value: date,
          }));
        });
    },
  });

  const currentPage = useCurrentPageFromUrl();

  const loadTemplatesMutation = useMutation({
    mutationFn: async (data: LoadTemplatesSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/a7/a7_fia_actions/templates`,
        {
          reportingDate: formattedDate ?? "",
          prefix: "a7_fia",
          client: "a7_fia",
          action_button_name: t("actions.load_templates"),
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success(t("templates.load_started"));
      setIsOpen(false);
      reset();
    },
    onError: () => {
      toast.error(t("templates.load_error"));
    },
  });

  const onSubmit = (data: LoadTemplatesSchemaType) => {
    loadTemplatesMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage:
              "linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("actions.load_templates")}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
          <div>
            {dateOptions && (
              <AutocompleteField
                field={{
                  name: "reportingDate",
                  label: t("templates.reporting_date"),
                  type: "autocomplete",
                  options: dateOptions,
                }}
                setValue={setValue as any}
                watch={watch}
              />
            )}
          </div>
          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!isValid}
              isLoading={loadTemplatesMutation.isPending}
            >
              {t("actions.load")}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
};

export default A7FIALoadTemplates;
